import { useState } from 'react';
import Papa from 'papaparse';

const useCSVHandler = () => {
    const [importedData, setImportedData] = useState<any[]>([]);
    const [isLoadingImport, setIsLoadingImport] = useState(false);

    const csvToArray = (text: string): string[][] => {
        const parseResult = Papa.parse(text);
        // Normalize boolean values: remove tick prefix and handle any-case TRUE/FALSE
        return parseResult.data.map((row: string[]) =>
            row.map((cell: string) => {
                if (typeof cell === 'string') {
                    // Strip leading tick that Excel/export adds to force text treatment,
                    // then do a case-insensitive check so any casing variant round-trips correctly
                    const stripped = cell.startsWith("'") ? cell.slice(1) : cell;
                    const lower = stripped.toLowerCase();
                    if (lower === 'true') return 'true';
                    if (lower === 'false') return 'false';
                }
                return cell;
            })
        );
    };

    const importCSV = (file: File, processCSVData: (csvArray: string[][]) => any[]) => {

        const reader = new FileReader();
        reader.onloadend = () => {
            const csvText = reader.result?.toString();
            if (csvText) {
                const csvArray = csvToArray(csvText);
                const importedFields = processCSVData(csvArray);
                setImportedData(importedFields);
            }
        };

        reader.readAsText(file);
    };

    const convertToCSVFriendly = (text: string) => {
        if (text == null || text === undefined) return "";

        const textStr = String(text);
        if (!textStr.length) return "";

        // Check if text starts with characters that Excel interprets as formulas
        const startsWithFormulaChar = /^[=+\-@]/.test(textStr);

        // Check if value is a boolean string that Excel would auto-convert
        const isBooleanLike = /^(true|false)$/i.test(textStr);

        // For boolean-like values, prefix with tick to force Excel to treat as text
        if (isBooleanLike) {
            return `"'${textStr}"`;
        }

        // Check if we need to wrap in quotes (contains comma, quotes, newlines, or starts with formula characters)
        if (textStr.includes(',') || textStr.includes('"') || textStr.includes('\n') || textStr.includes('\r') || startsWithFormulaChar) {
            // For formula characters, prefix with single quote to prevent Excel interpretation
            const escapedText = startsWithFormulaChar ? `'${textStr}` : textStr;
            // Wrap the entire text in double quotes and escape any existing double quotes
            return `"${escapedText.replace(/"/g, '""').replace(/\r?\n/g, ' ')}"`;
        }

        // If no special characters, return the text as-is
        return textStr;
    };

    const exportCSV = (data: any[], headers: string[], filename: string) => {

        let csvContent = `${headers.map(header => convertToCSVFriendly(header)).join(',')}\n`;

        data.forEach(item => {

            if (item?.associatedVariable?.length && item?.associatedVariable === item?.value) {
                return; // don't want shared topics fields that are marked as location specific in export
            } else if (item?.associatedVariable?.length && item?.associatedVariable !== item?.value) {
                delete item.associatedVariable;
            }

            // Use headers to ensure column order matches
            const row = headers.map(header => {
                const value = item[header];
                return convertToCSVFriendly(value != null ? String(value) : '');
            }).join(',');

            csvContent += row + '\n';

        });

        // Add BOM to ensure correct encoding
        const bom = '\uFEFF';
        const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();

    };


    return {
        importedData,
        isLoadingImport,
        setIsLoadingImport,
        importCSV,
        exportCSV
    };
};

export default useCSVHandler;
